import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import RealtimeGlobe from "@/components/globe/RealtimeGlobe";
import CommunityGlobeBachaz from "@/components/globe/CommunityGlobeBachaz";
import CommunityGallery from "@/components/community/CommunityGallery";
import ProfileDirectory from "@/components/profile/ProfileDirectory";
import CallToAction from "@/components/home/CallToAction";
import Tile from "@/components/shared/Tile";
import TileGrid from "@/components/shared/TileGrid";
import { Users2, Globe2, MapPin, Award } from "lucide-react";
import Footer from "@/components/layout/Footer";

const Community = () => {
  const globeSectionRef = useRef<HTMLElement>(null);
  const [communityStats, setCommunityStats] = useState({
    members: 0,
    countries: 0,
    cities: 0,
    loading: true
  });

  // Fetch community stats from the same data source as the globe
  useEffect(() => {
    fetch("https://cbuxrdarzwtolyjhnhzw.supabase.co/storage/v1/object/public/globe-cache/community_map.json")
      .then(res => res.json())
      .then(data => {
        const users = Array.isArray(data) ? data : data.data;
        
        // Calculate unique cities and countries
        const uniqueCountries = new Set(users.map((u: any) => u.country).filter(Boolean));
        const uniqueCities = new Set(users.map((u: any) => u.city).filter(Boolean));
        
        setCommunityStats({
          members: users.length,
          countries: uniqueCountries.size,
          cities: uniqueCities.size,
          loading: false
        });
      })
      .catch(err => {
        setCommunityStats(prev => ({ ...prev, loading: false }));
      });
  }, []);

  const isMobile = useIsMobile();
  
  return (
    <main className={`relative pt-20 lg:pt-[88px] ${!isMobile ? 'bg-gradient-to-b from-black via-[#0a0a0f] to-black' : ''}`}>
      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden flex items-center justify-center">
        {/* Gallery Background */}
        <div className="absolute inset-0">
          <CommunityGallery />
        </div>
        
        {/* Dark overlay for better visibility - Disabled on mobile */}
        <div
          className="absolute inset-0 pointer-events-none hidden md:block"
          style={{
            background: "linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.75) 50%, rgba(0,0,0,0.85) 100%)",
            zIndex: 5,
          }}
        />

        {/* Hero Title */}
        <motion.div
          className="relative text-center px-4 max-w-5xl mx-auto pointer-events-none"
          style={{ zIndex: 10, marginTop: '-200px' }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.p
            className="text-lg md:text-xl mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            style={{
              fontFamily: "'Grift', sans-serif",
              color: "rgba(255, 255, 255, 0.7)",
              letterSpacing: "0.05em",
            }}
          >
            Welcome to
          </motion.p>

          <motion.h1
            className="leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(40px, 8vw, 80px)",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
              textShadow: "0 4px 20px rgba(0, 0, 0, 0.8)",
            }}
          >
            The{" "}
            <span className="inline-block relative">
              <span style={{ color: "#E67E22" }}>Endless Community</span>
              {/* Animated Underline */}
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{ backgroundColor: "#E67E22" }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 1.3 }}
              />
            </span>
          </motion.h1>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-16 md:bottom-24 left-1/2 transform -translate-x-1/2 pointer-events-auto cursor-pointer"
          style={{ zIndex: 20 }}
          onClick={() => {
            window.scrollBy({ top: window.innerHeight, behavior: "smooth" });
          }}
        >
          <div className="flex flex-col items-center gap-2">
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-6 h-10 border-2 border-white/60 rounded-full flex items-start justify-center p-2 bg-black/20 backdrop-blur-sm"
            >
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="w-1 h-2 bg-white rounded-full"
              />
            </motion.div>
            <motion.p
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="text-white/70 text-xs font-medium"
            >
              Scroll to explore
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="relative py-20 px-4 bg-gradient-to-b from-black via-[#0a0a0f] to-black">
        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
              style={{
                fontFamily: "'Grift', sans-serif",
                color: "#FFFFFF",
                letterSpacing: "-0.02em",
              }}
            >
              Our <span style={{ color: "#E67E22" }}>Global Community</span>
            </h2>
            <p
              className="text-lg text-white/70 max-w-2xl mx-auto"
              style={{
                fontFamily: "'Grift', sans-serif",
              }}
            >
              Join our global dance community spanning {communityStats.countries}+ countries
            </p>
          </motion.div>

          <TileGrid columns={4} gap="lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Tile
                icon={Users2}
                title={communityStats.loading ? "..." : `${communityStats.members.toLocaleString()}`}
                description="Community Members"
                variant="glass"
                hoverable
                spotlight
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Tile
                icon={Globe2}
                title={communityStats.loading ? "..." : `${communityStats.countries}`}
                description="Countries"
                variant="glass"
                hoverable
                spotlight
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Tile
                icon={MapPin}
                title={communityStats.loading ? "..." : `${communityStats.cities}`}
                description="Cities Worldwide"
                variant="glass"
                hoverable
                spotlight
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Tile
                icon={Award}
                title="200+"
                description="Events & Workshops"
                variant="glass"
                hoverable
                spotlight
              />
            </motion.div>
          </TileGrid>
        </div>
      </section>

      {/* Join Now CTA Section */}
      <section className="relative py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Main CTA Text */}
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-2xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Grift', sans-serif",
                color: "#FFFFFF",
              }}
            >
              Ready to Join the Movement?
            </motion.h3>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-white/70 mb-8"
              style={{
                fontFamily: "'Grift', sans-serif",
              }}
            >
              Become part of our global dance family and transform your bachata journey
            </motion.p>

            {/* Join Now Button */}
            <motion.a
              href="/pricing"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full relative overflow-hidden group cursor-pointer"
              style={{
                background: "linear-gradient(135deg, #E67E22 0%, #F39C12 100%)",
                boxShadow: "0 0 30px rgba(230, 126, 34, 0.5), 0 10px 40px rgba(0, 0, 0, 0.3)",
                border: "none",
                fontFamily: "'Grift', sans-serif",
                fontSize: "18px",
                fontWeight: 600,
                color: "#FFFFFF",
                letterSpacing: "0.02em",
              }}
            >
              {/* Animated gradient overlay */}
              <motion.div
                className="absolute inset-0"
                initial={{ x: "-100%" }}
                whileHover={{ x: "100%" }}
                transition={{ duration: 0.6 }}
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                }}
              />
              
              <span className="relative z-10">Join Now</span>
              
              {/* Arrow with animation */}
              <motion.svg
                className="relative z-10"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                animate={{
                  x: [0, 5, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <path
                  d="M4 10H16M16 10L11 5M16 10L11 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>

              {/* Glow effect */}
              <motion.div
                className="absolute inset-0 rounded-full"
                initial={{ scale: 1, opacity: 0 }}
                whileHover={{ scale: 1.5, opacity: 0.3 }}
                transition={{ duration: 0.6 }}
                style={{
                  background: "radial-gradient(circle, #E67E22, transparent)",
                }}
              />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Globe Section */}
      <section 
        ref={globeSectionRef}
        className="relative min-h-screen w-full overflow-hidden py-20"
      >
        {/* Two Column Layout - Left takes 60%, Right takes 40% */}
        <div className="max-w-[1600px] mx-auto px-4 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8 items-center h-full">
          {/* Left Column - Mapbox Globe (60% width) */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full h-[600px] lg:h-[800px]"
          >
            <CommunityGlobeBachaz />
          </motion.div>

          {/* Right Column - Realtime Globe (40% width) */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full flex items-center justify-center"
          >
            <RealtimeGlobe />
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CallToAction />

      {/* Profile Directory Section */}
      <ProfileDirectory />
    </main>
  );
};

export default Community;


